/* @flow */
/* eslint-env es6,browser */

import { getRandom } from '../utils';
import {
  isDNT,
  isJSONSupported,
  inIframe,
  getViewportDimensions
} from '../utils/browserutil';
import { logError } from '../utils/logger';
import { extend, isObject } from '../utils/coreutils';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { getUserID } from '../idproviders/idputils';
import {
  PREBID_UID_PUBLINKID,
  PREBID_UID_LIVERAMP,
  PREBID_UID_PUBCOMMON,
  PREBID_UID_CRITEO,
  PREBID_UID_UNIFIEDID,
  PREBID_UID_LOTAMEPANORAMAID,
  PREBID_UID_PARRABLEID,
  PREBID_UID_FABRICK
} from '../idproviders/idpconstants';

/**
 * Flow typedef
 */
declare type oxPosData = {
  aus: string,
  dddid: string,
  auid: string
};

declare type oxSiteData = {
  ju: string,
  jr: string,
  ch: string,
  res: string,
  ifr: string,
  tz: number,
  tws: string,
  be: 1,
  bc: 'hb_pb_2.1.7',
  nocache: number,
  ns: 1 | 0,
  lre?: string,
  pubcid?: string,
  neustarid?: string,
  criteoid?: string
};

declare type oxResponsePosData = {
  adunitid: number,
  html: string,
  pub_rev: string
};
declare type oxResponse = {
  ads: {
    count: number,
    ad: Array<oxResponsePosData>
  }
};

/**
 * @module bidders/openx
 */
/**
 * @global
 * @description extends {@link hbConfig}
 * @property {string} [qpKey=openx] qp key
 * @property {string} id ID
 */
/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */
/**
 * @description Implements functionality for openx header bidding
 * @exports openxbid
 * @augments _bidbase
 * @requires module:utils
 * @requires module:bidmanager
 * @requires module:fetch
 * @requires module:adconfig
 */

class _openx extends bidbase {
  _DefConfig: openxBidConfig;

  _config: openxBidConfig;

  constructor() {
    super('openx');
    this._DefConfig = extend(true, {}, this._DefConfig, {
      bidUrl: '//uol-d.openx.net/w/1.0/arj',
      qpKey: 'openx'
    });
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['bidUrl'];
  }

  // eslint-disable-next-line valid-jsdoc
  /**
   * Process bid response. Response format:
   *
   * @param {Array<string>} posList - HB position list
   * @param {object} response - Response from server
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {object.<string, hbPosData>} - Return data
   * @access private
   */
  _processBidResponse(
    posList: Array<string>,
    response: oxResponse,
    ret: hbPartnerData
  ): hbPartnerData {
    const { count = 0, ad: ads = [] } =
      (isObject(response) && response.ads) || {};
    if (count <= 0 || ads.length <= 0) {
      return ret;
    }

    // eslint-disable-next-line unicorn/prefer-object-from-entries
    const auidToPos: { [key: number]: string } = posList.reduce((acc, pos) => {
      acc[this._config.posTokenMap[pos].auid] = pos || '';
      return acc;
    }, {});
    ads.forEach((ad: oxResponsePosData) => {
      const { adunitid = 0, html: creative = '', pub_rev: cpm = '' } = ad;
      const position = auidToPos[adunitid] || '';
      if (
        adunitid > 0 &&
        position.length > 0 &&
        creative.length > 0 &&
        cpm.length
      ) {
        this._handleBidPosData(
          ret,
          position,
          String((Number(cpm) || 0) / 1000),
          String(adunitid),
          creative
        );
      }
    });
    return ret;
  }

  /* Get IDP info (User ID provider values) for bid request */
  _getIDPInfo(): anyObject {
    const UID_MAP: { [UID: string]: string } = {
      [PREBID_UID_PUBLINKID]: 'publinkid',
      [PREBID_UID_LIVERAMP]: 'lre',
      [PREBID_UID_FABRICK]: 'nuestarid',
      [PREBID_UID_CRITEO]: 'criteoid',
      [PREBID_UID_PUBCOMMON]: 'pubcid',
      [PREBID_UID_LOTAMEPANORAMAID]: 'lotameid',
      [PREBID_UID_PARRABLEID]: 'parrableid',
      [PREBID_UID_UNIFIEDID]: 'ttduuid'
    };
    // get supported userID value and append only the valid one
    return Object.fromEntries<string, string>( // $FlowFixMe[infer-error]
      Object.entries(UID_MAP)
        // $FlowFixMe
        .map(([uid, mappedValue]) => [mappedValue, getUserID(uid)])
        .filter(([, value]) => value.length > 0)
    );
  }

  _getSiteDetails(): oxSiteData {
    const siteInfo = extend(
      {
        ju: document.location.href || '',
        jr: document.referrer || '',
        // eslint-disable-next-line compat/compat
        ch: document.characterSet || '',
        res: `${window.screen.width}x${window.screen.height}x${
          window.screen.colorDepth || ''
        }`,
        ifr: inIframe() ? 'true' : 'false',
        tz: new Date().getTimezoneOffset(),
        tws: getViewportDimensions().join('x'),
        be: 1,
        bc: 'hb_pb_2.1.7',
        nocache: Date.now(),
        ns: isDNT() ? 1 : 0
      },
      this._getIDPInfo() // Get Available IDP (from User ID modules) information
    );

    return siteInfo;
  }

  /**
   * Returns sizes suported by a position
   *
   * @param {Array<string>} position - Ad positions
   * @returns {string} - Sizes supported by the position as a string
   * @access private
   */
  _getPositionSizes(position: string = ''): string {
    if (!position.length) {
      return '';
    }
    return this._config.posTokenMap[position].sizes
      .map(([w, h]) => `${w}x${h}`)
      .join(',');
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {object} - Position data object
   * @access private
   */
  _getPositionData(posList: Array<string>): oxPosData {
    const posData = {
      aus: posList.map(pos => this._getPositionSizes(pos)).join('|'),
      dddid: posList.map(() => getRandom()).join(','),
      auid: posList
        .map(pos => this._config.posTokenMap[pos].auid || '')
        .join(',')
    };
    return posData;
  }

  /**
   * Builds and sends bid requests
   *
   * @param {Array<string>} posList - Ad positions
   * @returns {Promise} - Returns promise
   * @access private
   */
  _getBids(posList: Array<string>): Promise<oxResponse> {
    const siteData = this._getSiteDetails();
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: posList.join(',')
    };
    const req = {
      method: 'GET',
      credentials: 'include'
    };
    const posData = this._getPositionData(posList);
    opt.data = extend(true, {}, siteData, posData, this._getPrivacyString());
    return fetchJSON(this._config.bidUrl, opt, req).then(response => response);
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB Position list
   * @param {object.<string, hbPosData>} ret - Return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    return this._getBids(posList).then(
      response => this._processBidResponse(posList, response, ret),
      error => {
        logError(`${this._name} Failed to get bids: ${error || ''}`);
        return ret;
      }
    );
  }
}

// add bidder to bidmanager
const openx: _openx = new _openx();
export default openx;
